import React, { useState, useMemo, useRef, useEffect } from 'react';
import cn from 'classnames';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { previewCatalogTool, processCatalogTool } from '../../../api/catalog_tool';
import ScopeSelector from './ScopeSelector';
import BulkPasteCard from './BulkPasteCard';
import BulkRunReport from './BulkRunReport';
import PinDiagnosticPanel from './PinDiagnosticPanel';
import DomainRemovalCard from './DomainRemovalCard';
import './BulkUnlinkTab.scss';

const MAX_LINKS = 1000;

const POST_ACTIONS = [
  { value: 'requeue', title: 'Re-queue for Matching', description: 'Return to the matching queue for manual review' },
  { value: 'dismiss', title: 'Dismiss', description: 'Hide these URLs from matching queues — per-URL, no data deleted' },
  {
    value: 'archive',
    title: 'Archive',
    description: "Hide these URLs' entire domains from all matching queues — for counterfeit sites like temu.com. Domain-wide, no data deleted."
  }
];

/** Bulk Unlink: scope → paste exact URLs → unlink pins + variants, with re-queue/dismiss/archive post-actions. */
const BulkUnlinkTab = () => {
  const [scopeType, setScopeType] = useState('brand');
  const [selected, setSelected] = useState(null);
  const [links, setLinks] = useState('');
  const [preview, setPreview] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [postAction, setPostAction] = useState('requeue');
  const [processing, setProcessing] = useState(false);
  const [report, setReport] = useState(null);

  const lines = useMemo(() => links.split('\n').map(line => line.trim()).filter(Boolean), [links]);
  const overLimit = lines.length > MAX_LINKS;

  // Batch validation rides one debounced preview call; a bumped id invalidates stale responses.
  const latestPreviewId = useRef(0);
  const previewDebounced = useMemo(
    () =>
      AwesomeDebouncePromise(async body => {
        const previewId = ++latestPreviewId.current;
        const response = await previewCatalogTool(body);
        return { previewId, data: response.data };
      }, 600),
    []
  );

  useEffect(() => {
    if (!selected || !lines.length || overLimit) {
      latestPreviewId.current++;
      setPreview(null);
      setIsPreviewing(false);
      return;
    }
    setIsPreviewing(true);
    previewDebounced({ links: lines, mode: 'unlink', scope: { type: selected.type, id: selected.id } })
      .then(({ previewId, data }) => {
        if (previewId !== latestPreviewId.current) return;
        setPreview(data);
        setIsPreviewing(false);
      })
      .catch(error => {
        setPreview(null);
        setIsPreviewing(false);
        window.ALERT.error(error || 'Failed to validate links');
      });
  }, [lines, selected, overLimit, previewDebounced]);

  const validCount = preview?.summary?.valid || 0;
  const invalidCount = preview?.summary?.invalid || 0;

  const disabledReason = !selected
    ? 'Select a brand or product to scope this run'
    : !lines.length
    ? 'Paste at least one URL'
    : overLimit
    ? `Over the ${MAX_LINKS.toLocaleString()}-link limit`
    : isPreviewing
    ? 'Checking links…'
    : invalidCount > 0
    ? 'Fix or remove invalid lines first'
    : validCount === 0
    ? 'Nothing processable in this batch'
    : null;

  const runProcess = async archiveDomains => {
    setProcessing(true);
    try {
      const response = await processCatalogTool({
        links: lines,
        mode: 'unlink',
        postAction,
        scope: { type: selected.type, id: selected.id },
        ...(archiveDomains ? { archiveDomains } : {})
      });
      setReport(response.data);
      setLinks('');
      setPreview(null);
      window.ALERT.success(`Processed ${response.data.processed} links`);
    } catch (error) {
      window.ALERT.error(error || 'Failed to process links');
    } finally {
      setProcessing(false);
    }
  };

  // Archive is domain-wide (the ignored-domains list), so it alone gets a confirm dialog.
  // The confirmed list rides along to the server, which archives nothing outside it.
  const handleProcess = () => {
    if (postAction !== 'archive') return runProcess();
    const archivableStatuses = ['ok', 'not_linked'];
    const domains = [
      ...new Set(
        (preview?.rows || [])
          .filter(row => archivableStatuses.includes(row.status))
          .map(row => row.domain)
          .filter(Boolean)
      )
    ];
    confirmAlert({
      title: 'Archive entire domains?',
      message: `This hides ${domains.length === 1 ? 'the domain' : `${domains.length} domains`} ${domains.join(', ')} from all matching queues — every URL on ${
        domains.length === 1 ? 'this domain' : 'these domains'
      }, not just the pasted ones. No data is deleted.`,
      buttons: [
        { label: 'Cancel', onClick: () => {} },
        { label: 'Archive', onClick: () => runProcess(domains) }
      ]
    });
  };

  const clearScope = () => {
    setSelected(null);
    setPreview(null);
    setReport(null);
  };

  const isPinScope = scopeType === 'pin';

  return (
    <div className='catalog-unlink-tab'>
      <div className={cn('tab-columns', { 'single-column': isPinScope })}>
        <div className='left-column'>
          <section className='scope-section'>
            <h2>1 · Scope</h2>
            <ScopeSelector
              mode='unlink'
              scopeType={scopeType}
              onScopeTypeChange={setScopeType}
              selected={selected}
              onSelect={setSelected}
              onClear={clearScope}
            />
          </section>

          {isPinScope ? (
            <PinDiagnosticPanel />
          ) : (
            <BulkPasteCard
              mode='unlink'
              heading='2 · Paste exact URLs, one per line'
              links={links}
              onChange={setLinks}
              previewRows={preview?.rows}
              summary={preview?.summary}
              isPreviewing={isPreviewing}
              maxLinks={MAX_LINKS}
            />
          )}
        </div>

        {!isPinScope && (
          <div className='right-column'>
            <h2>3 · Action</h2>

            <div className='action-info-card'>
              <span className='info-title'>Unlink pins + variants</span>
              <span className='info-description'>
                Remove the product match from these URLs — updates associated pins and variants. Always both — no toggle.
              </span>
            </div>

            <h2 className='rail-subheading'>After unlinking</h2>
            <div className='option-group'>
              {POST_ACTIONS.map(action => (
                <label key={action.value} className='option'>
                  <input
                    type='radio'
                    name='ct-postAction'
                    value={action.value}
                    checked={postAction === action.value}
                    onChange={e => setPostAction(e.target.value)}
                  />
                  <div className='option-content'>
                    <span className='option-title'>{action.title}</span>
                    <span className='option-description'>{action.description}</span>
                  </div>
                </label>
              ))}
            </div>

            <button className='submit-button' onClick={handleProcess} disabled={processing || !!disabledReason}>
              {processing ? 'Processing…' : validCount > 0 ? `Process ${validCount.toLocaleString()} links` : 'Process links'}
            </button>
            {!processing && disabledReason && <div className='cta-reason'>{disabledReason}</div>}

            {selected?.type === 'brand' && <DomainRemovalCard brand={selected} postAction={postAction} onReport={setReport} />}
          </div>
        )}
      </div>

      {report && <BulkRunReport mode='unlink' report={report} />}
    </div>
  );
};

export default BulkUnlinkTab;
